<template>
  <button
    :class="[
      'flex items-center justify-center gap-2 cursor-pointer rounded-lg transition-colors',
      sizeClasses,
      colorClasses,
      isDisabled ? 'opacity-50 cursor-not-allowed' : '',
      classes,
    ]"
    :tabindex="isDisabled ? -1 : 0"
    :disabled="isDisabled"
    @click.stop="handleClick">
    <q-icon v-if="icon" :name="icon" :size="iconSize" />
    <span v-if="text">{{ text }}</span>
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  text?: string;
  icon?: string;
  color?: string;
  size?: 'sm' | 'md' | 'lg';
  isDisabled?: boolean;
  classes?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  text: '',
  icon: '',
  color: 'primary',
  size: 'md',
  isDisabled: false,
  classes: () => [],
});

const emit = defineEmits(['submit', 'click']);

const isDisabled = computed(() => props.isDisabled);

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'px-3 py-2 h-8 text-sm font-medium';
    case 'lg':
      return 'px-4 py-2 h-10 text-base font-bold';
    default:
      return 'px-3 py-2 h-10 text-sm font-medium';
  }
});

const colorClasses = computed(() => {
  switch (props.color) {
    case 'secondary':
      return 'bg-main-secondary border border-text-secondary text-text-primary';
    case 'white':
      return 'bg-white border border-input-stroke text-text-primary';
    default:
      return 'bg-primary text-white hover:bg-main-hover';
  }
});

const iconSize = computed(() => {
  switch (props.size) {
    case 'sm':
      return '20px';
    case 'lg':
      return '24px';
    default:
      return '20px';
  }
});

const handleClick = () => {
  emit('submit');
  emit('click');
};
</script>
